import { KeyboardEvent, useEffect, useId, useRef } from 'react';
import { ExportDataset, ExportDatasetGroup, ExportDatasetKey } from './exportDatasets';

interface ExportDatasetPickerProps {
  datasets: ExportDataset[];
  value: ExportDatasetKey;
  onChange: (value: ExportDatasetKey) => void;
  /** One unit in scope: "per unit" wording doesn't apply. */
  singleUnit?: boolean;
  disabled?: boolean;
}

const GROUP_ORDER: ExportDatasetGroup[] = ['totals', 'perUnit'];

export default function ExportDatasetPicker({
  datasets,
  value,
  onChange,
  singleUnit = false,
  disabled = false,
}: ExportDatasetPickerProps) {
  const baseId = useId();
  const tabRefs = useRef<Partial<Record<ExportDatasetGroup, HTMLButtonElement | null>>>({});
  // Last choice per tab, so switching back restores it.
  const lastChoice = useRef<Partial<Record<ExportDatasetGroup, ExportDatasetKey>>>({});

  const groups = GROUP_ORDER.filter(group => datasets.some(d => d.group === group));
  const activeGroup = datasets.find(d => d.key === value)?.group ?? groups[0];
  useEffect(() => {
    lastChoice.current[activeGroup] = value;
  }, [activeGroup, value]);
  const items = datasets.filter(d => d.group === activeGroup);

  const groupLabel = (group: ExportDatasetGroup) =>
    group === 'totals' ? 'Totals' : singleUnit ? 'Detailed' : 'Per unit';

  const selectGroup = (group: ExportDatasetGroup) => {
    if (group === activeGroup) return;
    const remembered = lastChoice.current[group];
    const next = datasets.find(d => d.key === remembered) ?? datasets.find(d => d.group === group);
    if (next) onChange(next.key);
  };

  const onTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    const index = groups.indexOf(activeGroup);
    const next =
      groups[(index + (event.key === 'ArrowRight' ? 1 : -1) + groups.length) % groups.length];
    selectGroup(next);
    tabRefs.current[next]?.focus();
  };

  const list = (
    <fieldset disabled={disabled}>
      <legend className="sr-only">{groupLabel(activeGroup)}</legend>
      <div className="overflow-hidden rounded-lg border border-[var(--anl-border)]">
        {items.map(dataset => (
          <label
            key={dataset.key}
            className="flex cursor-pointer items-start gap-3 border-b border-l-2 border-b-[var(--anl-border)] border-l-transparent px-3 py-2.5 transition-colors last:border-b-0 hover:bg-[var(--anl-surface-elev)] has-[:checked]:border-l-[var(--anl-accent)] has-[:checked]:bg-[var(--anl-surface-elev)] has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-inset has-[:focus-visible]:ring-[var(--anl-accent)]"
          >
            <input
              type="radio"
              name="export-dataset"
              value={dataset.key}
              checked={dataset.key === value}
              onChange={() => onChange(dataset.key)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-[var(--anl-accent)] focus:outline-none"
            />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-[var(--anl-text)]">
                {dataset.label}
              </span>
              <span className="block text-xs text-[var(--anl-text-muted)]">
                {singleUnit ? dataset.singleUnitDescription : dataset.description}
              </span>
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );

  // Only totals available (no per-unit export permission): no tabs to choose between.
  if (groups.length < 2) return list;

  return (
    <div className="space-y-3">
      <div
        role="tablist"
        aria-label="Export type"
        className="grid grid-cols-2 gap-1 rounded-lg border border-[var(--anl-border)] p-1"
      >
        {groups.map(group => {
          const selected = group === activeGroup;
          return (
            <button
              key={group}
              ref={el => {
                tabRefs.current[group] = el;
              }}
              type="button"
              role="tab"
              id={`${baseId}-tab-${group}`}
              aria-selected={selected}
              aria-controls={`${baseId}-panel`}
              tabIndex={selected ? 0 : -1}
              disabled={disabled}
              onClick={() => selectGroup(group)}
              onKeyDown={onTabKeyDown}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--anl-accent)] disabled:cursor-not-allowed disabled:opacity-50 ${
                selected
                  ? 'bg-[var(--anl-surface-elev)] text-[var(--anl-text)] shadow-sm'
                  : 'text-[var(--anl-text-muted)] hover:text-[var(--anl-text)]'
              }`}
            >
              {groupLabel(group)}
            </button>
          );
        })}
      </div>
      <div role="tabpanel" id={`${baseId}-panel`} aria-labelledby={`${baseId}-tab-${activeGroup}`}>
        {list}
      </div>
    </div>
  );
}
